import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  DatePicker,
  Row,
  Col,
  Select,
  message,
  Spin,
  Tag,
  Statistic,
  Modal,
  Tabs,
  Alert,
  Typography,
  Icon,
} from 'antd';
import moment from 'moment';
import { apiGet, apiPost } from './utils/apiService';
import { fetchDailyIotSummary } from './utils/fuelService';

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;
const { Title, Text } = Typography;
const { Option } = Select;

// Variance % above which a row is flagged
const VARIANCE_FLAG_PCT = 5;
// Reconciliation match window (minutes) between an issue entry and a sensor dispense event
const MATCH_WINDOW_MIN = 15;

// CSV export helper
const escapeCsv = (val) => {
  if (val === null || val === undefined) return '';
  const s = String(val);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const downloadCsv = (filename, header, rows) => {
  const lines = [header.map(escapeCsv).join(',')];
  rows.forEach((r) => lines.push(r.map(escapeCsv).join(',')));
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const extractName = (field) => {
  if (!field) return null;
  if (typeof field === 'string') return field.trim();
  if (typeof field === 'object') {
    return field.name || field.code || field.title || null;
  }
  return null;
};

const fmtNum = (v, d = 2) => (typeof v === 'number' && Number.isFinite(v) ? v.toFixed(d) : '-');
const fmtDateTime = (ts) => (ts ? moment(ts).format('DD-MMM-YYYY HH:mm') : '-');
const fmtDate = (ts) => (ts ? moment(ts).format('DD-MMM-YYYY') : '-');

// Detect a bowser from an asset record (mirrors MonthlyFullTankReport.js logic)
const isBowserAsset = (asset) => {
  if (asset.equipmentSubType && typeof asset.equipmentSubType === 'object') {
    const at = asset.equipmentSubType.assetType;
    if (at && (at.isBowser === true || at.isBowser === 'true')) return true;
  }
  if (asset.assetType && typeof asset.assetType === 'object') {
    const at = asset.assetType;
    if (
      at.isBowser === true ||
      at.isBowser === 'true' ||
      at.isFuelBowser === true ||
      at.isAFuelBowser === true ||
      at['Is a fuel Bowser'] === true
    ) {
      return true;
    }
  }
  return false;
};

const buildBowserMeta = (asset) => {
  const assetTypeName =
    extractName(asset.equipmentSubType?.assetType) || extractName(asset.assetType) || '-';
  const assetSubTypeName =
    extractName(asset.equipmentSubType) || extractName(asset.assetSubType) || '-';
  const assetModelName = extractName(asset.assetModel) || '-';
  const locationName = extractName(asset.location) || '-';
  const subLocationName = extractName(asset.subLocation) || '-';
  return {
    _id: asset._id,
    assetCode: asset.assetCode,
    assetName: asset.assetName || asset.altName || '',
    assetTypeName,
    assetSubTypeName,
    assetModelName,
    locationName,
    subLocationName,
  };
};

const reconcileEvents = (records, dispenseEvents) => {
  const issues = (records || [])
    .filter((r) => Number(r.quantityRemoved) > 0)
    .map((r, i) => ({
      idx: i,
      ts: r.datetime ? moment(r.datetime).valueOf() : null,
      qty: Number(r.quantityRemoved) || 0,
      voucherNo: r.refFuelRecord?.voucherNo || r.voucherNo || '-',
      refAssetCode: r.refAsset?.assetCode || '-',
      matched: false,
      matchedEvent: null,
    }));
  const events = (dispenseEvents || []).map((e, i) => ({
    ...e,
    idx: i,
    matched: false,
    matchedIssueIdx: null,
  }));

  const windowMs = MATCH_WINDOW_MIN * 60 * 1000;
  issues.forEach((iss) => {
    if (iss.ts === null) return;
    let bestIdx = -1;
    let bestDelta = Infinity;
    events.forEach((ev, j) => {
      if (ev.matched) return;
      const evCenter = ev.ts || (ev.startTs && ev.endTs ? (ev.startTs + ev.endTs) / 2 : null);
      if (evCenter === null) return;
      const dt = Math.abs(evCenter - iss.ts);
      if (dt <= windowMs && dt < bestDelta) {
        bestDelta = dt;
        bestIdx = j;
      }
    });
    if (bestIdx >= 0) {
      iss.matched = true;
      iss.matchedEvent = events[bestIdx];
      events[bestIdx].matched = true;
      events[bestIdx].matchedIssueIdx = iss.idx;
    }
  });
  return { issues, events };
};

const BowserComparisonReport = () => {
  const [dateRange, setDateRange] = useState([
    moment().subtract(7, 'days').startOf('day'),
    moment().endOf('day'),
  ]);

  const [allBowsers, setAllBowsers] = useState([]); // bowser meta records
  const [loadingAssets, setLoadingAssets] = useState(false);

  // Filter selections
  const [fLocation, setFLocation] = useState(null);
  const [fSubLocation, setFSubLocation] = useState(null);
  const [fAssetType, setFAssetType] = useState(null);
  const [fAssetSubType, setFAssetSubType] = useState(null);
  const [fAssetModel, setFAssetModel] = useState(null);
  const [fAssetCodes, setFAssetCodes] = useState([]); // multi-select

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [rows, setRows] = useState([]);
  const [bowserDataMap, setBowserDataMap] = useState({});
  const [drillRow, setDrillRow] = useState(null);

  // Load asset list once and build bowser meta records
  useEffect(() => {
    const loadAssets = async () => {
      setLoadingAssets(true);
      try {
        const res = await apiGet('/asset/asset-all', { headers: { Accept: '*/*' } });
        if (!res.success) {
          message.error(res.error || 'Failed to load asset list');
          return;
        }
        const assets = res.data || [];
        const bowsers = assets.filter(isBowserAsset).map(buildBowserMeta);
        const list = bowsers.length > 0 ? bowsers : assets.map(buildBowserMeta);
        console.log(
          `Loaded ${assets.length} assets, detected ${bowsers.length} bowsers via isBowser flag, using ${list.length} for filter dropdowns`
        );
        if (assets.length > 0) {
          console.log('Sample asset record:', assets[0]);
        }
        setAllBowsers(list);
      } catch (e) {
        console.error(e);
        message.error('Failed to load assets');
      } finally {
        setLoadingAssets(false);
      }
    };
    loadAssets();
  }, []);

  // Apply cascading filters to derive currently visible bowsers
  const filteredBowsers = useMemo(() => {
    return allBowsers.filter((b) => {
      if (fLocation && b.locationName !== fLocation) return false;
      if (fSubLocation && b.subLocationName !== fSubLocation) return false;
      if (fAssetType && b.assetTypeName !== fAssetType) return false;
      if (fAssetSubType && b.assetSubTypeName !== fAssetSubType) return false;
      if (fAssetModel && b.assetModelName !== fAssetModel) return false;
      if (fAssetCodes.length && !fAssetCodes.includes(b.assetCode)) return false;
      return true;
    });
  }, [allBowsers, fLocation, fSubLocation, fAssetType, fAssetSubType, fAssetModel, fAssetCodes]);

  const uniq = (arr) => [...new Set(arr.filter((v) => v && v !== '-'))].sort();

  // Cascading dropdown options derived from current selections
  const locationOptions = useMemo(() => uniq(allBowsers.map((b) => b.locationName)), [allBowsers]);
  const subLocationOptions = useMemo(
    () =>
      uniq(
        allBowsers
          .filter((b) => !fLocation || b.locationName === fLocation)
          .map((b) => b.subLocationName)
      ),
    [allBowsers, fLocation]
  );
  const assetTypeOptions = useMemo(() => uniq(allBowsers.map((b) => b.assetTypeName)), [allBowsers]);
  const assetSubTypeOptions = useMemo(
    () =>
      uniq(
        allBowsers
          .filter((b) => !fAssetType || b.assetTypeName === fAssetType)
          .map((b) => b.assetSubTypeName)
      ),
    [allBowsers, fAssetType]
  );
  const assetModelOptions = useMemo(
    () =>
      uniq(
        allBowsers
          .filter((b) => !fAssetType || b.assetTypeName === fAssetType)
          .filter((b) => !fAssetSubType || b.assetSubTypeName === fAssetSubType)
          .map((b) => b.assetModelName)
      ),
    [allBowsers, fAssetType, fAssetSubType]
  );
  const assetCodeOptions = useMemo(() => filteredBowsers.map((b) => b.assetCode), [filteredBowsers]);

  const resetFilters = () => {
    setFLocation(null);
    setFSubLocation(null);
    setFAssetType(null);
    setFAssetSubType(null);
    setFAssetModel(null);
    setFAssetCodes([]);
  };

  const handleGenerate = async () => {
    if (!dateRange || dateRange.length !== 2) {
      message.warning('Please pick a date range');
      return;
    }
    if (filteredBowsers.length === 0) {
      message.warning('No bowsers match the current filters');
      return;
    }

    const periodFrom = dateRange[0].valueOf();
    const periodTo = dateRange[1].valueOf();
    const assetIds = filteredBowsers.map((b) => b._id).filter(Boolean);

    setLoading(true);
    setProgress(`Fetching bowser report for ${assetIds.length} bowser(s)...`);
    setRows([]);
    setBowserDataMap({});

    try {
      const payload = {
        locations: [],
        subLocations: [],
        assetTypes: [],
        assets: assetIds,
        periodFrom,
        periodTo,
      };
      console.log('Bowser report payload:', payload);
      const res = await apiPost('/fuel/bowser-report', payload);
      console.log('Bowser report response:', res);
      if (!res.success) {
        message.error(res.error || 'Failed to fetch bowser report');
        setLoading(false);
        return;
      }
      const data = res.data || {};
      const bowserIds = Object.keys(data);
      if (bowserIds.length === 0) {
        message.info('No bowser data for the selected period');
        setLoading(false);
        return;
      }

      const bowserMetaById = {};
      filteredBowsers.forEach((b) => {
        bowserMetaById[b._id] = b;
      });

      const builtRows = [];
      const builtMap = {};

      for (let i = 0; i < bowserIds.length; i++) {
        const id = bowserIds[i];
        const entry = data[id] || {};
        const meta = entry.data || {};
        const records = Array.isArray(entry.records) ? entry.records : [];
        const localMeta = bowserMetaById[id] || {};

        const assetCode = meta.assetCode || localMeta.assetCode || '-';
        const assetName = meta.assetName || meta.name || localMeta.assetName || '';
        const location = extractName(meta.location) || localMeta.locationName || '-';

        setProgress(`Fetching sensor summary for ${assetCode} (${i + 1}/${bowserIds.length})...`);

        // Fetch daily IoT summary for this bowser - aggregated daily fuel data
        let sensorDispense = 0;
        let sensorRefill = 0;
        let sensorDrain = 0;
        let dailyRecords = [];
        let sensorError = null;
        let hasSensorData = false;

        try {
          const summary = await fetchDailyIotSummary(id, periodFrom, periodTo);
          console.log(`Daily summary for ${assetCode}:`, summary);
          if (summary.success && Array.isArray(summary.data) && summary.data.length > 0) {
            dailyRecords = summary.data;
            hasSensorData = true;
            dailyRecords.forEach((d) => {
              const consumed = Number(d.fuel_consumed ?? d.fuel_consumed_analog ?? 0) || 0;
              const refuel = Number(d.total_refuel ?? 0) || 0;
              const theft = Number(d.total_theft ?? 0) || 0;
              sensorDispense += Math.abs(consumed);
              sensorRefill += Math.abs(refuel);
              sensorDrain += Math.abs(theft);
            });
          } else if (!summary.success) {
            sensorError = summary.error;
          }
        } catch (e) {
          sensorError = e.message;
          console.error(`Sensor fetch failed for ${assetCode}`, e);
        }

        const totalIssued =
          typeof meta.totalIssued === 'number'
            ? meta.totalIssued
            : records
                .filter((r) => Number(r.quantityRemoved) > 0)
                .reduce((s, r) => s + Number(r.quantityRemoved || 0), 0);
        const totalAdded =
          typeof meta.totalAdded === 'number'
            ? meta.totalAdded
            : records
                .filter((r) => Number(r.quantityAdded) > 0)
                .reduce((s, r) => s + Number(r.quantityAdded || 0), 0);

        const variance = sensorDispense - totalIssued;
        const variancePct = totalIssued > 0 ? (variance / totalIssued) * 100 : null;

        const row = {
          key: id,
          bowserId: id,
          assetCode,
          assetName,
          location,
          openingBalance: meta.openingBalance,
          balanceAtEnd: meta.balanceAtEnd ?? meta.closingBalance,
          totalAdded,
          sensorRefill,
          totalIssued,
          sensorDispense,
          sensorDrain,
          variance,
          variancePct,
          recordsCount: records.length,
          hasSensor: hasSensorData,
          sensorError,
          dailyCount: dailyRecords.length,
        };

        // Build dispense events from daily records (one per day)
        const dispenseEvents = dailyRecords
          .map((d) => {
            const qty = Math.abs(Number(d.fuel_consumed ?? d.fuel_consumed_analog ?? 0) || 0);
            const dayStart =
              d.startOfDayEpoch || d.dataFromTimestamp || (d.date ? moment(d.date).valueOf() : null);
            const dayEnd =
              d.endOfDayEpoch || d.dataToTimestamp || (d.date ? moment(d.date).endOf('day').valueOf() : null);
            return {
              ts: dayStart,
              startTs: dayStart,
              endTs: dayEnd,
              qty,
              kind: 'dispense',
            };
          })
          .filter((e) => e.qty > 0);

        const refillEvents = dailyRecords
          .map((d) => {
            const qty = Math.abs(Number(d.total_refuel ?? 0) || 0);
            const dayStart =
              d.startOfDayEpoch || d.dataFromTimestamp || (d.date ? moment(d.date).valueOf() : null);
            const dayEnd =
              d.endOfDayEpoch || d.dataToTimestamp || (d.date ? moment(d.date).endOf('day').valueOf() : null);
            return {
              ts: dayStart,
              startTs: dayStart,
              endTs: dayEnd,
              qty,
              kind: 'refill',
            };
          })
          .filter((e) => e.qty > 0);

        builtRows.push(row);
        builtMap[id] = { row, records, dispenseEvents, refillEvents, dailyRecords };
      }

      setRows(builtRows);
      setBowserDataMap(builtMap);
      setProgress('');
    } catch (e) {
      console.error(e);
      message.error(e.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    if (!rows.length) return;
    const header = [
      'Bowser Code',
      'Bowser Name',
      'Location',
      'Opening',
      'Closing',
      'Total Added (API)',
      'Sensor Refill',
      'Total Issued (API)',
      'Sensor Dispense',
      'Sensor Drain',
      'Variance (L)',
      'Variance %',
      'Records',
      'Has Sensor',
    ];
    const out = rows.map((r) => [
      r.assetCode,
      r.assetName,
      r.location,
      fmtNum(r.openingBalance),
      fmtNum(r.balanceAtEnd),
      fmtNum(r.totalAdded),
      fmtNum(r.sensorRefill),
      fmtNum(r.totalIssued),
      fmtNum(r.sensorDispense),
      fmtNum(r.sensorDrain),
      fmtNum(r.variance),
      r.variancePct === null ? '-' : `${r.variancePct.toFixed(2)}%`,
      r.recordsCount,
      r.hasSensor ? 'Yes' : 'No',
    ]);
    downloadCsv(`bowser-comparison-${moment().format('YYYYMMDD-HHmm')}.csv`, header, out);
  };

  const columns = [
    {
      title: 'Bowser',
      dataIndex: 'assetCode',
      key: 'assetCode',
      sorter: (a, b) => (a.assetCode || '').localeCompare(b.assetCode || ''),
      fixed: 'left',
    },
    { title: 'Name', dataIndex: 'assetName', key: 'assetName' },
    { title: 'Location', dataIndex: 'location', key: 'location' },
    { title: 'Opening (L)', dataIndex: 'openingBalance', key: 'openingBalance', render: (v) => fmtNum(v) },
    { title: 'Closing (L)', dataIndex: 'balanceAtEnd', key: 'balanceAtEnd', render: (v) => fmtNum(v) },
    {
      title: 'Added (API)',
      dataIndex: 'totalAdded',
      key: 'totalAdded',
      render: (v) => fmtNum(v),
      sorter: (a, b) => (a.totalAdded || 0) - (b.totalAdded || 0),
    },
    {
      title: 'Sensor Refill',
      dataIndex: 'sensorRefill',
      key: 'sensorRefill',
      render: (v, r) => (r.hasSensor ? fmtNum(v) : '-'),
    },
    {
      title: 'Issued (API)',
      dataIndex: 'totalIssued',
      key: 'totalIssued',
      render: (v) => fmtNum(v),
      sorter: (a, b) => (a.totalIssued || 0) - (b.totalIssued || 0),
    },
    {
      title: 'Sensor Dispense',
      dataIndex: 'sensorDispense',
      key: 'sensorDispense',
      render: (v, r) => (r.hasSensor ? fmtNum(v) : '-'),
      sorter: (a, b) => (a.sensorDispense || 0) - (b.sensorDispense || 0),
    },
    {
      title: 'Variance',
      dataIndex: 'variance',
      key: 'variance',
      render: (v, r) => {
        if (!r.hasSensor) return <Tag>No sensor data</Tag>;
        const flag = r.variancePct !== null && Math.abs(r.variancePct) > VARIANCE_FLAG_PCT;
        return (
          <Tag color={flag ? 'red' : 'green'}>
            {fmtNum(v)} {r.variancePct !== null ? `(${r.variancePct.toFixed(1)}%)` : ''}
          </Tag>
        );
      },
      sorter: (a, b) => (a.variance || 0) - (b.variance || 0),
    },
    { title: 'Records', dataIndex: 'recordsCount', key: 'recordsCount' },
    {
      title: 'Days w/ data',
      dataIndex: 'dailyCount',
      key: 'dailyCount',
      render: (v, r) => (r.hasSensor ? v : '-'),
    },
  ];

  // Summary aggregates
  const totals = rows.reduce(
    (acc, r) => {
      acc.bowsers += 1;
      acc.issued += r.totalIssued || 0;
      acc.dispense += r.sensorDispense || 0;
      if (r.hasSensor) acc.withSensor += 1;
      return acc;
    },
    { bowsers: 0, issued: 0, dispense: 0, withSensor: 0 }
  );
  const aggVariance = totals.dispense - totals.issued;
  const aggVarPct = totals.issued > 0 ? (aggVariance / totals.issued) * 100 : null;

  // Drilldown content
  const drillContent = () => {
    if (!drillRow) return null;
    const detail = bowserDataMap[drillRow.bowserId];
    if (!detail) return null;
    const { records, dispenseEvents, refillEvents, dailyRecords } = detail;
    const { issues, events } = reconcileEvents(records, dispenseEvents);

    return (
      <Tabs defaultActiveKey="issues">
        <TabPane tab={`Issue Entries (${records.length})`} key="issues">
          <Table
            size="small"
            rowKey={(r, i) => i}
            pagination={{ pageSize: 20 }}
            dataSource={records}
            columns={[
              {
                title: 'Date/Time',
                dataIndex: 'datetime',
                render: (v) => fmtDateTime(v ? moment(v).valueOf() : null),
              },
              {
                title: 'Voucher',
                render: (_, r) => r.refFuelRecord?.voucherNo || r.voucherNo || '-',
              },
              { title: 'Ref Asset', render: (_, r) => r.refAsset?.assetCode || '-' },
              { title: 'Added', dataIndex: 'quantityAdded', render: (v) => fmtNum(v) },
              { title: 'Removed', dataIndex: 'quantityRemoved', render: (v) => fmtNum(v) },
              { title: 'Balance', dataIndex: 'balance', render: (v) => fmtNum(v) },
            ]}
          />
        </TabPane>
        <TabPane tab={`Sensor Daily (${dailyRecords.length})`} key="sensor">
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Daily fuel summary aggregated from IoT sensor data."
          />
          <Table
            size="small"
            rowKey={(r, i) => `d${i}`}
            pagination={{ pageSize: 31 }}
            dataSource={dailyRecords}
            columns={[
              {
                title: 'Date',
                render: (_, r) =>
                  fmtDate(r.startOfDayEpoch || r.dataFromTimestamp || (r.date ? moment(r.date).valueOf() : null)),
              },
              {
                title: 'Fuel Consumed (L)',
                render: (_, r) =>
                  fmtNum(Math.abs(Number(r.fuel_consumed ?? r.fuel_consumed_analog ?? 0) || 0)),
              },
              {
                title: 'Refuel (L)',
                render: (_, r) => fmtNum(Math.abs(Number(r.total_refuel ?? 0) || 0)),
              },
              {
                title: 'Theft/Drain (L)',
                render: (_, r) => fmtNum(Math.abs(Number(r.total_theft ?? 0) || 0)),
              },
              {
                title: 'Distance (km)',
                render: (_, r) => fmtNum(((r.distance_travelled || 0) / 1000)),
              },
            ]}
          />
        </TabPane>
        <TabPane tab="Reconciliation (daily)" key="recon">
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Daily totals: sum of issue entry quantities vs IoT sensor daily dispense for the same day."
          />
          {(() => {
            // Aggregate issue entries by day
            const issueByDay = {};
            (records || [])
              .filter((r) => Number(r.quantityRemoved) > 0)
              .forEach((r) => {
                if (!r.datetime) return;
                const day = moment(r.datetime).format('YYYY-MM-DD');
                if (!issueByDay[day]) issueByDay[day] = { qty: 0, count: 0 };
                issueByDay[day].qty += Number(r.quantityRemoved) || 0;
                issueByDay[day].count += 1;
              });

            // Aggregate sensor dispense by day
            const sensorByDay = {};
            dispenseEvents.forEach((e) => {
              if (!e.startTs) return;
              const day = moment(e.startTs).format('YYYY-MM-DD');
              if (!sensorByDay[day]) sensorByDay[day] = 0;
              sensorByDay[day] += e.qty || 0;
            });

            const allDays = [...new Set([...Object.keys(issueByDay), ...Object.keys(sensorByDay)])].sort();
            const dailyRows = allDays.map((day) => {
              const issued = issueByDay[day]?.qty || 0;
              const count = issueByDay[day]?.count || 0;
              const sensor = sensorByDay[day] || 0;
              const variance = sensor - issued;
              const variancePct = issued > 0 ? (variance / issued) * 100 : null;
              return { day, issued, count, sensor, variance, variancePct };
            });

            const totals = dailyRows.reduce(
              (acc, r) => {
                acc.issued += r.issued;
                acc.sensor += r.sensor;
                acc.count += r.count;
                return acc;
              },
              { issued: 0, sensor: 0, count: 0 }
            );
            const totalVar = totals.sensor - totals.issued;
            const totalVarPct = totals.issued > 0 ? (totalVar / totals.issued) * 100 : null;

            return (
              <Table
                size="small"
                rowKey="day"
                pagination={false}
                dataSource={dailyRows}
                columns={[
                  { title: 'Day', dataIndex: 'day', render: (v) => fmtDate(moment(v).valueOf()) },
                  { title: 'Issue Entries', dataIndex: 'count' },
                  { title: 'Issued (L)', dataIndex: 'issued', render: (v) => fmtNum(v) },
                  { title: 'Sensor Dispense (L)', dataIndex: 'sensor', render: (v) => fmtNum(v) },
                  {
                    title: 'Variance (L)',
                    dataIndex: 'variance',
                    render: (v, r) => {
                      const flag = r.variancePct !== null && Math.abs(r.variancePct) > VARIANCE_FLAG_PCT;
                      return (
                        <Tag color={flag ? 'red' : 'green'}>
                          {fmtNum(v)}
                          {r.variancePct !== null ? ` (${r.variancePct.toFixed(1)}%)` : ''}
                        </Tag>
                      );
                    },
                  },
                ]}
                footer={() => (
                  <Row gutter={16}>
                    <Col span={6}>
                      <strong>Total entries:</strong> {totals.count}
                    </Col>
                    <Col span={6}>
                      <strong>Total issued:</strong> {fmtNum(totals.issued)} L
                    </Col>
                    <Col span={6}>
                      <strong>Total sensor:</strong> {fmtNum(totals.sensor)} L
                    </Col>
                    <Col span={6}>
                      <strong>Variance:</strong> {fmtNum(totalVar)} L
                      {totalVarPct !== null ? ` (${totalVarPct.toFixed(1)}%)` : ''}
                    </Col>
                  </Row>
                )}
              />
            );
          })()}
        </TabPane>
      </Tabs>
    );
  };

  const filterCol = { xs: 24, sm: 12, md: 8, lg: 6, xl: 4 };

  return (
    <div style={{ padding: 16 }}>
      <Card
        title={
          <span>
            <Icon type="reconciliation" /> Bowser Dispense vs Issue Comparison
          </span>
        }
      >
        <Spin spinning={loadingAssets} tip="Loading bowser list...">
          <Row gutter={[12, 12]}>
            <Col {...filterCol}>
              <Text strong>Period</Text>
              <RangePicker
                style={{ width: '100%', marginTop: 4 }}
                value={dateRange}
                onChange={setDateRange}
                ranges={{
                  'Last 7 Days': [moment().subtract(7, 'days'), moment()],
                  'Last 30 Days': [moment().subtract(30, 'days'), moment()],
                  'This Month': [moment().startOf('month'), moment().endOf('month')],
                }}
              />
            </Col>
            <Col {...filterCol}>
              <Text strong>Location</Text>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                placeholder="All locations"
                allowClear
                showSearch
                value={fLocation}
                onChange={(v) => {
                  setFLocation(v);
                  setFSubLocation(null);
                }}
              >
                {locationOptions.map((v) => (
                  <Option key={v} value={v}>
                    {v}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col {...filterCol}>
              <Text strong>Sub Location</Text>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                placeholder="All sub locations"
                allowClear
                showSearch
                value={fSubLocation}
                onChange={setFSubLocation}
              >
                {subLocationOptions.map((v) => (
                  <Option key={v} value={v}>
                    {v}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col {...filterCol}>
              <Text strong>Asset Type</Text>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                placeholder="All types"
                allowClear
                showSearch
                value={fAssetType}
                onChange={(v) => {
                  setFAssetType(v);
                  setFAssetSubType(null);
                  setFAssetModel(null);
                }}
              >
                {assetTypeOptions.map((v) => (
                  <Option key={v} value={v}>
                    {v}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col {...filterCol}>
              <Text strong>Asset Sub Type</Text>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                placeholder="All sub types"
                allowClear
                showSearch
                value={fAssetSubType}
                onChange={(v) => {
                  setFAssetSubType(v);
                  setFAssetModel(null);
                }}
              >
                {assetSubTypeOptions.map((v) => (
                  <Option key={v} value={v}>
                    {v}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col {...filterCol}>
              <Text strong>Asset Model</Text>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                placeholder="All models"
                allowClear
                showSearch
                value={fAssetModel}
                onChange={setFAssetModel}
              >
                {assetModelOptions.map((v) => (
                  <Option key={v} value={v}>
                    {v}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={24} md={16} lg={12} xl={8}>
              <Text strong>Asset Code</Text>
              <Select
                mode="multiple"
                style={{ width: '100%', marginTop: 4 }}
                placeholder="All bowsers (filtered above)"
                allowClear
                showSearch
                value={fAssetCodes}
                onChange={setFAssetCodes}
                maxTagCount={3}
              >
                {assetCodeOptions.map((v) => (
                  <Option key={v} value={v}>
                    {v}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={24} md={8} lg={6} xl={4}>
              <div style={{ marginTop: 22 }}>
                <Button type="primary" icon="play-circle" onClick={handleGenerate} loading={loading}>
                  Generate
                </Button>{' '}
                <Button icon="reload" onClick={resetFilters}>
                  Reset
                </Button>{' '}
                <Button icon="download" onClick={exportCsv} disabled={!rows.length}>
                  CSV
                </Button>
              </div>
            </Col>
          </Row>
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">
              {filteredBowsers.length} bowser(s) match current filters
              {allBowsers.length ? ` (of ${allBowsers.length} total)` : ''}
            </Text>
          </div>
        </Spin>

        {loading && (
          <div style={{ marginTop: 16 }}>
            <Spin /> <Text type="secondary">{progress}</Text>
          </div>
        )}

        {rows.length > 0 && (
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={5}>
              <Card size="small">
                <Statistic
                  title="Bowsers"
                  value={totals.bowsers}
                  suffix={`(${totals.withSensor} w/ sensor)`}
                />
              </Card>
            </Col>
            <Col span={5}>
              <Card size="small">
                <Statistic title="Total Issued (L)" value={Number(totals.issued.toFixed(2))} />
              </Card>
            </Col>
            <Col span={5}>
              <Card size="small">
                <Statistic
                  title="Sensor Dispense (L)"
                  value={Number(totals.dispense.toFixed(2))}
                />
              </Card>
            </Col>
            <Col span={9}>
              <Card size="small">
                <Statistic
                  title="Aggregate Variance"
                  value={Number(aggVariance.toFixed(2))}
                  suffix={aggVarPct === null ? 'L' : `L (${aggVarPct.toFixed(2)}%)`}
                  valueStyle={{
                    color:
                      aggVarPct !== null && Math.abs(aggVarPct) > VARIANCE_FLAG_PCT
                        ? '#cf1322'
                        : '#3f8600',
                  }}
                />
              </Card>
            </Col>
          </Row>
        )}

        <Table
          style={{ marginTop: 16 }}
          rowKey="key"
          dataSource={rows}
          columns={columns}
          pagination={{ pageSize: 20 }}
          size="middle"
          scroll={{ x: 1400 }}
          onRow={(record) => ({
            onClick: () => setDrillRow(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      <Modal
        title={
          drillRow
            ? `Bowser Detail — ${drillRow.assetCode} ${drillRow.assetName || ''}`
            : ''
        }
        visible={!!drillRow}
        onCancel={() => setDrillRow(null)}
        footer={null}
        width={1100}
        destroyOnClose
      >
        {drillContent()}
      </Modal>
    </div>
  );
};

export default BowserComparisonReport;
